import { ThemeProvider } from "@emotion/react";
import { CssBaseline, styled } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { FormManage } from "./form/form-manage";
import { FormOriginalManage } from "./form/formOriginal-manage";
import { Forms } from "./pages/form/forms";
import { Mortgages } from "./pages/mortgages/mortgages";
import { Quotes } from "./pages/quotes/quotes";
import { BrandEnum } from "./_common/enum/brand.enum";
import { resolveBrandFromUrl } from "./_const/brandOrigin.const";
import {
    applyPersistedParentContext,
    bootstrapParamsFromReferrer,
    drainEarlyParentContextQueue,
    getConfiguredParentOrigins,
    getParentOrigin,
    handleParentContextMessage,
    requestParentContextFromIframe,
    resolveBrandFromParentOrigin,
} from "./_helpers/parentContext.helper";
import { getThemeForBrand } from "./_styles/brand.theme.styles";

const useStyles = {
    appScreen: {
        display: "flex",
        backgroundColor: "#fff",
        height: "100%",
    } as React.CSSProperties,
};

const Main = styled("main")(({ theme }) => ({
    flexGrow: 1,
    width: "100%",
    maxWidth: "100%",
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    boxSizing: "border-box",
}));

function BrandedApp({ brand }: { brand: BrandEnum }) {
    const theme = useMemo(() => getThemeForBrand(brand), [brand]);

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <div style={{ ...useStyles.appScreen, position: "relative" }} id="app-screen">
                <Main>
                    <BrowserRouter>
                        <Routes>
                            <Route path="/" element={<Navigate to="/Forms" replace />} />
                            <Route path="/Quotes" element={<Quotes brand={brand} />} />
                            <Route path="/Quotes/:productName/:formId/" element={<Quotes brand={brand} />} />
                            <Route path="/Mortgages/:formId" element={<Mortgages brand={brand} />} />
                            <Route path="/Forms" element={<Forms brand={brand} />} />
                            <Route path="/Forms/:formId" element={<FormManage brand={brand} />} />
                            <Route path="/Forms/:formId/:clientId" element={<FormManage brand={brand} />} />
                            <Route path="/FormOriginal/:formId" element={<FormOriginalManage brand={brand} />} />
                            <Route path="/FormOriginal/:formId/:quoteId" element={<FormOriginalManage brand={brand} />} />
                            <Route path="/FormOriginal" element={<FormOriginalManage brand={brand} />} />
                        </Routes>
                    </BrowserRouter>
                </Main>
                <div id="lq-turnstile" style={{ position: "fixed", width: 1, height: 1, overflow: "hidden", opacity: 0, pointerEvents: "none" }} />
            </div>
        </ThemeProvider>
    );
}

function App() {
    const [activeBrand, setActiveBrand] = useState<BrandEnum | null>(null);
    const bootstrapPathRef = useRef("");
    const bootstrapQueryRef = useRef(new URLSearchParams(""));

    function trySetBrandFromUrl(rawUrl: string) {
        const matched = resolveBrandFromUrl(rawUrl);
        if (matched) {
            setActiveBrand((current) => current ?? matched);
        }
    }

    useEffect(() => {
        const isFramed = window.self !== window.top;
        if (!isFramed) return;

        const pathnameRef = { current: bootstrapPathRef.current };
        const queryParamsRef = { current: bootstrapQueryRef.current };

        drainEarlyParentContextQueue(pathnameRef, queryParamsRef, (origin) => {
            const brand = resolveBrandFromParentOrigin(origin);
            if (brand) setActiveBrand((current) => current ?? brand);
        });

        applyPersistedParentContext(pathnameRef, queryParamsRef);
        bootstrapPathRef.current = pathnameRef.current;
        bootstrapQueryRef.current = queryParamsRef.current;

        const detected = getParentOrigin();
        if (detected) {
            trySetBrandFromUrl(detected);
        }

        const boot = bootstrapParamsFromReferrer();
        if (boot) {
            trySetBrandFromUrl(document.referrer);
        }

        requestParentContextFromIframe();

        const interval = window.setInterval(() => {
            requestParentContextFromIframe();
        }, 300);

        function onMessage(e: MessageEvent) {
            const allowed = getConfiguredParentOrigins();
            const pathnameRefMsg = { current: bootstrapPathRef.current };
            const queryParamsRefMsg = { current: bootstrapQueryRef.current };

            const result = handleParentContextMessage(
                e,
                pathnameRefMsg,
                queryParamsRefMsg,
                allowed
            );

            bootstrapPathRef.current = pathnameRefMsg.current;
            bootstrapQueryRef.current = queryParamsRefMsg.current;

            if (result.brand) {
                setActiveBrand((current) => current ?? result.brand!);
            } else if (e.origin) {
                const brand = resolveBrandFromParentOrigin(e.origin);
                if (brand) setActiveBrand((current) => current ?? brand);
            }

            if (e.data && (e.data as { type?: string }).type === "host-origin") {
                const raw = (e.data as { origin?: string }).origin || e.origin;
                trySetBrandFromUrl(raw);
            }
        }

        window.addEventListener("message", onMessage);

        return () => {
            window.clearInterval(interval);
            window.removeEventListener("message", onMessage);
        };
    }, []);

    if (!activeBrand) {
        return null;
    }

    return <BrandedApp brand={activeBrand} />;
}

export default App;
